import React, { useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { translations } from "../../utils/translations.js";
import { HiShoppingCart } from "react-icons/hi";
import { FaRegUser } from "react-icons/fa6";
import LanguageComponent from "./LanguageComponent.js";

import Dropdown from "./core-components/Dropdown.tsx";
import { RiLogoutBoxRLine } from "react-icons/ri";
import { useAuth } from "../../hooks/useAuth.jsx";
import { SlMenu } from "react-icons/sl";
import CategoriesComponent from "./CategoriesComponent.tsx";
import { Link, useNavigate } from "react-router-dom";
import Search from "./core-components/Search.tsx";

const menuPages = [
  {
    name: "header-menu-projects",
    url: "/projects",
  },
  {
    name: "header-menu-colors",
    url: "/colors",
  },
  {
    name: "header-menu-support",
    url: "/support",
  },
];

const Logo = styled.img`
  width: 9rem;
  height: auto;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 6rem;
  }

  @media (max-width: 400px) {
    width: 7rem;
  }
`;

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 5%;
  width: 90%;
  padding: 20px 0;
  color: var(--tertiary-font-color);
`;
const MenuContainer = styled.div`
  color: var(--tertiary-font-color);
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 2rem;
`;

const MenuItem = styled(Link)`
  color: var(--secondary-font-color);
  font-weight: bold;
  font-size: 1rem;
  font-family: Georgia, serif;
  cursor: pointer;
  text-decoration: none;
  align-items: center;
  display: flex;
  text-align: center;
  justify-content: center;

  transition: transform 0.1s ease-in-out;

  &:hover {
    transform: scale(1.1);
    color: var(--primary-font-color);
  }
`;

const ItemsGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`;

const Button = styled(Link)<{ size?: string }>`
  font-size: ${(props) => props.size || "1.5rem"};
  color: var(--secondary-font-color);
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  padding: 0 4px;

  &:hover {
    transform: scale(1.2);
  }
`;

const Logout = styled.button`
  color: red;
  background-color: white;
  border: none;
  align-items: center;
  justify-content: center;
  display: flex;
  gap: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-family: Georgia, serif;
`;

const EmagLogo = styled.img`
  width: 4.5rem;
  height: auto;
`;

const MobileMenu = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
  padding: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);

  @media (min-width: 560px) {
    flex-direction: row;
  }
`;

const MobileHeadeMenu = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 2rem;
`;

const CartItems = styled.div`
  background-color: var(--secondary-green);
  color: white;
  align-items: center;
  font-size: 10px;
  padding: 1px;
  border-radius: 50%;
  margin-left: -6px;
  margin-top: -6px;
  display: flex;
  justify-content: center;
  width: 12px;
  height: 12px;
  text-decoration: none;
`;

interface InputFieldProps {
  isMobile: boolean;
}

const HeaderMenu: React.FC<InputFieldProps> = ({ isMobile }) => {
  const navigate = useNavigate();
  const { logout, token, tokenRole } = useAuth();
  const languageCode = useSelector((state: any) => state.settings.languageCode);
  const products = useSelector((state: any) => state.data.cartProducts);
  const [productsNumber, setProductsNumber] = React.useState(0);

  useEffect(() => {
    let prdNumber = 0;
    products.forEach((product: any) => {
      prdNumber += product.quantity;
    });
    setProductsNumber(prdNumber);
  }, [products, setProductsNumber]);

  const handleAccountClick = (e, isOpen: boolean) => {
    e.stopPropagation();
    if (token) navigate("/account");
    else navigate("/login");
  };
  const menuContent = () => {
    return (
      <MobileHeadeMenu>
        {menuPages.map((page, index) => (
          <MenuItem key={index + page.name} to={page.url}>
            {translations[languageCode][page.name] ?? page.name}
          </MenuItem>
        ))}
        {!isMobile && "|"}
        <MenuItem
          to={
            "https://www.emag.ro/vendors/vendor/arthrxhm?ref=seller-page-see-all-products"
          }
        >
          <EmagLogo src={"/images/emag-logo.png"} alt="emag" />
        </MenuItem>
      </MobileHeadeMenu>
    );
  };

  const LogoutButton: React.FC<{ closeDropdown?: () => void }> = ({
    closeDropdown,
  }) => {
    return (
      <Logout
        onClick={(e) => {
          // Call the logout function and close the dropdown

          logout();
          closeDropdown && closeDropdown();

          e.stopPropagation();
        }}
      >
        <RiLogoutBoxRLine /> Logout
      </Logout>
    );
  };

  return (
    <MenuWrapper data-testid="header-menu">
      {isMobile ? (
        <>
          <Dropdown
            icon={<SlMenu />}
            content={
              <>
                <MobileMenu data-testid="mobile-header-menu">
                  {menuContent()}
                  <Search />
                </MobileMenu>

                <CategoriesComponent />
              </>
            }
          />
          <a href="/">
            <Logo src={"/images/header-logo.png"} alt="Logo" />
          </a>
        </>
      ) : (
        <>
          <a href="/">
            <Logo src={"/images/header-logo.png"} alt="Logo" />
          </a>
          <MenuContainer>
            <Dropdown
              name="Shop"
              size="1rem"
              content={<CategoriesComponent />}
            />
            {menuContent()}
          </MenuContainer>
        </>
      )}

      <ItemsGroup>
        {!isMobile && <Search />}
        <LanguageComponent />
        <Dropdown
          icon={<FaRegUser />}
          autoWidth
          content={<LogoutButton />}
          onClick={handleAccountClick}
          hoverActive={token && tokenRole === "Client"}
        />

        <Button size="1.6rem" style={{ display: "flex" }} to={"/cart"}>
          <HiShoppingCart />
          {products.length > 0 && <CartItems>{productsNumber}</CartItems>}
        </Button>
      </ItemsGroup>
    </MenuWrapper>
  );
};

export default HeaderMenu;
